import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CScoreChart from '../components/CScoreChart';
import ScoreCell from '../components/ScoreCell';
import Avatar from '../components/Avatar';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScoring } from '../lib/scoring';
import { SkeletonChart, SkeletonTable, SkeletonHeader } from '../components/Skeleton';

export default function Dashboard() {
  const { profile } = useAuth();
  const { threshold, weights } = useScoring();
  const [search, setSearch] = useState('');
  const [clinics, setClinics] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    Promise.all([api.dashboard.chart(), api.clinics.list()])
      .then(([chart, clinicsList]) => {
        setChartData(chart);
        setClinics(clinicsList);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const filteredClinics = clinics.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div>
      <SkeletonHeader />
      <SkeletonChart />
      <div className="mt-6"><SkeletonTable rows={3} showSubtitle /></div>
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-6">Dashboard</h1>

      {chartData.length > 0 && <CScoreChart data={chartData} />}

      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mt-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-bold text-text">All Clinics</h3>
            <p className="text-sm text-gray-500 mt-0.5">Quickly view the health of each clinic</p>
          </div>
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-200 rounded-lg px-4 py-2 text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
          />
        </div>

        {/* Desktop table */}
        <table className="w-full hidden sm:table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
              <th className="text-center py-3 text-sm font-semibold text-gray-700 w-48">Average CScore</th>
            </tr>
          </thead>
          <tbody>
            {filteredClinics.map((clinic) => (
              <tr key={clinic.id} className="border-b border-gray-50 last:border-0">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <Avatar src={clinic.image_url} name={clinic.name} size="md" />
                    <div>
                      <Link
                        to={`/clinics/${clinic.id}`}
                        className="text-primary font-medium hover:underline text-sm"
                      >
                        {clinic.name}
                      </Link>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {clinic.clinician_count} Clinician{clinic.clinician_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 text-center">
                  <ScoreCell
                    score={clinic.avg_cscore}
                    breakdown={clinic.breakdown}
                    weights={weights}
                    threshold={threshold}
                    title={`${clinic.name} average`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile list */}
        <div className="sm:hidden">
          {filteredClinics.map((clinic, idx) => (
            <div key={clinic.id} className={`flex items-center justify-between py-3 ${idx !== filteredClinics.length - 1 ? 'border-b border-gray-100' : ''}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={clinic.image_url} name={clinic.name} size="md" />
                <div className="min-w-0">
                  <Link to={`/clinics/${clinic.id}`} className="text-primary font-medium hover:underline text-sm truncate block">
                    {clinic.name}
                  </Link>
                  <p className="text-xs text-gray-500">{clinic.clinician_count} Clinician{clinic.clinician_count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ScoreCell
                score={clinic.avg_cscore}
                breakdown={clinic.breakdown}
                weights={weights}
                threshold={threshold}
                title={`${clinic.name} average`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
